import React, { useState } from "react";
import { Box, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography, CircularProgress } from "@mui/material";
import { ReplicateIniChanges } from "system/types/interfaces";

interface TicketDetailsModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (subject: string, ticketDescription: string) => Promise<void>;
  isSubmitting: boolean;
  initialSubject?: string;
  initialDescription?: string;
  pendingReplicateChanges?: ReplicateIniChanges;
}

const TicketDetailsModal: React.FC<TicketDetailsModalProps> = ({
  open,
  onClose,
  onSubmit,
  isSubmitting,
  initialSubject,
  initialDescription,
  pendingReplicateChanges = { additions: [], removals: [] },
}) => {
  const [subject, setSubject] = useState("");
  const [ticketDescription, setTicketDescription] = useState("");

  React.useEffect(() => {
    if (open) {
      setSubject(initialSubject ?? "Policy editor change - ");
      setTicketDescription(initialDescription ?? "");
    }
  }, [open, initialSubject, initialDescription]);

  const handleOnClose = () => {
    setSubject("");
    setTicketDescription("");
    onClose();
  };

  const totalChanges = pendingReplicateChanges.additions.length + pendingReplicateChanges.removals.length;

  return (
    <Dialog open={open} onClose={handleOnClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: "#1b1a19", color: "#ffffff" }}>Ticket Details</DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: "#1b1a19",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <TextField
          autoFocus
          margin="dense"
          label="Subject"
          type="text"
          fullWidth
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{ "& .MuiInputBase-input": { color: "#ffffff" }, label: { color: "#A19F9D" }, mt: 2 }}
        />
        <TextField
          margin="dense"
          label="Ticket Description"
          type="text"
          fullWidth
          multiline
          minRows={3}
          value={ticketDescription}
          onChange={(e) => setTicketDescription(e.target.value)}
          sx={{ "& .MuiInputBase-input": { color: "#ffffff" }, label: { color: "#A19F9D" } }}
        />
        {totalChanges > 0 && (
          <Box sx={{ mt: 1, p: 1.5, border: "1px solid #605e5c", borderRadius: 1, backgroundColor: "#2b2a29" }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Typography variant="subtitle2" sx={{ color: "#0078D4" }}>
                Library changes requested
              </Typography>
              <Chip
                label={totalChanges}
                size="small"
                sx={{ backgroundColor: "#0078D4", color: "#fff", height: 18, fontSize: "0.65rem" }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: "#A19F9D", display: "block", mb: 1 }}>
              These Library changes will be included in the ticket body for Customer Success to apply.
            </Typography>
            {pendingReplicateChanges.additions.length > 0 && (
              <Box sx={{ mb: pendingReplicateChanges.removals.length > 0 ? 1 : 0 }}>
                <Typography variant="caption" sx={{ color: "#7fc97a", fontWeight: 600 }}>
                  Add ({pendingReplicateChanges.additions.length})
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, color: "#F3F2F1", maxHeight: 100, overflow: "auto" }}>
                  {pendingReplicateChanges.additions.map((pkg) => (
                    <li key={`add-${pkg}`}>
                      <Typography variant="body2">{pkg}</Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}
            {pendingReplicateChanges.removals.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ color: "#d5878b", fontWeight: 600 }}>
                  Remove ({pendingReplicateChanges.removals.length})
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, color: "#F3F2F1", maxHeight: 100, overflow: "auto" }}>
                  {pendingReplicateChanges.removals.map((pkg) => (
                    <li key={`rm-${pkg}`}>
                      <Typography variant="body2">{pkg}</Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#1b1a19", padding: 2 }}>
        <Button onClick={onClose} sx={{ color: "#ffffff", borderColor: "#757575" }}>
          Cancel
        </Button>
        <Button onClick={() => onSubmit(subject, ticketDescription)} variant="contained" disabled={isSubmitting}>
          {isSubmitting ? <CircularProgress size={24} /> : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketDetailsModal;
